import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch, setPreviewAsResolver } from "./api-client";
import type { PreviewAsResponse } from "@colab/shared";

const STORAGE_KEY = "colab.preview-as";
// Separate key so we don't bake admin-side UI state into the server-shaped
// PreviewAsResponse. The banner reads this on exit to bounce back to the
// page the admin came from instead of the generic /admin/dashboard.
const RETURN_TO_KEY = "colab.preview-as.return-to";

type PreviewAsState = PreviewAsResponse | null;

type EnterOptions = {
  // Where to send the admin when they hit "Back to admin" in the banner.
  // Captured by the caller before navigation so it's the URL they were on
  // when they clicked "Preview as partner", not a downstream /portal route.
  returnTo?: string;
};

type PreviewAsContextValue = {
  preview: PreviewAsState;
  returnTo: string | null;
  enter: (partnerId: string, options?: EnterOptions) => Promise<PreviewAsResponse>;
  exit: () => void;
};

const PreviewAsContext = createContext<PreviewAsContextValue | null>(null);

function loadFromStorage(): { preview: PreviewAsState; returnTo: string | null } {
  if (typeof sessionStorage === "undefined") return { preview: null, returnTo: null };
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return { preview: null, returnTo: null };
  try {
    const parsed = JSON.parse(raw) as PreviewAsResponse;
    if (new Date(parsed.expiresAt).getTime() < Date.now()) {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(RETURN_TO_KEY);
      return { preview: null, returnTo: null };
    }
    return {
      preview: parsed,
      returnTo: sessionStorage.getItem(RETURN_TO_KEY),
    };
  } catch {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
    return { preview: null, returnTo: null };
  }
}

export function PreviewAsProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  // Lazy init: `loadFromStorage` runs once on mount, not on every render.
  const [preview, setPreview] = useState<PreviewAsState>(
    () => loadFromStorage().preview,
  );
  const [returnTo, setReturnTo] = useState<string | null>(
    () => loadFromStorage().returnTo,
  );

  useEffect(() => {
    if (preview) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(preview));
    else sessionStorage.removeItem(STORAGE_KEY);
    // Register the module-level resolver every apiFetch caller consults when
    // no explicit `previewAs` option was passed. This is what makes every
    // portal hook ride the X-Preview-As header during preview-as navigation
    // without each call site threading the partnerId manually.
    setPreviewAsResolver(() => (preview ? preview.partnerId : null));
    return () => {
      setPreviewAsResolver(() => null);
    };
  }, [preview]);

  useEffect(() => {
    if (returnTo) sessionStorage.setItem(RETURN_TO_KEY, returnTo);
    else sessionStorage.removeItem(RETURN_TO_KEY);
  }, [returnTo]);

  const enterMutation = useMutation({
    mutationFn: (partnerId: string) =>
      apiFetch<PreviewAsResponse>("/auth/preview-as", {
        method: "POST",
        body: JSON.stringify({ partnerId }),
        // Opt out of automatic header injection — this IS the activation
        // call, so the partnerId we'd send is the one we haven't validated
        // yet. The worker derives the target partner from the body.
        previewAs: null,
      }),
    onSuccess: (data) => {
      setPreview(data);
      queryClient.invalidateQueries();
    },
  });

  const enter = async (partnerId: string, options?: EnterOptions) => {
    // Stash returnTo BEFORE the network call so it's available the moment
    // the new preview state is committed and the caller navigates into the
    // portal.
    setReturnTo(options?.returnTo ?? null);
    return enterMutation.mutateAsync(partnerId);
  };

  const exit = () => {
    setPreview(null);
    setReturnTo(null);
    queryClient.invalidateQueries();
  };

  return (
    <PreviewAsContext.Provider value={{ preview, returnTo, enter, exit }}>
      {children}
    </PreviewAsContext.Provider>
  );
}

export function usePreviewAs() {
  const ctx = useContext(PreviewAsContext);
  if (!ctx) throw new Error("usePreviewAs must be used inside <PreviewAsProvider>");
  return ctx;
}
